import Icon from 'flow/components/ui-components/Icon';
import Loading from 'flow/components/ui-components/Loading';
import { Attachment, NewAttachment } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import useAppContext from 'flow/hooks/useAppContext';
import {
    useState,
    useEffect,
    FC,
    LegacyRef,
    SyntheticEvent,
    KeyboardEventHandler,
    MouseEventHandler,
    useCallback,
    useRef,
} from 'react';

interface Props {
    src: string;
    alt?: string;
    className?: string;
    retryCount?: number;
    retryDelayMs?: number;
    fallbackSrc?: string;
    ref: LegacyRef<HTMLImageElement>;
    onLoad?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
    height: number;
    width?: number;
    onButtonClick?: MouseEventHandler<HTMLButtonElement>;
    onButtonKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
    onDownloadButton?: (error: boolean) => void;
    attachment?: TypedMap<Attachment> & TypedMap<NewAttachment>;
}

const PreviewImageWithRetry: FC<Props> = ({
    src,
    alt,
    className,
    retryCount = 5,
    retryDelayMs = 2000,
    fallbackSrc,
    ref,
    onLoad,
    height,
    width,
    onButtonClick,
    onButtonKeyDown,
    onDownloadButton,
    attachment,
}) => {
    const { i18next } = useAppContext();
    const [currentSrc, setCurrentSrc] = useState(src);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const attemptsRef = useRef(0);

    useEffect(() => {
        attemptsRef.current = 0;
        setIsLoading(true);
        setCurrentSrc(src);
        onDownloadButton?.(false);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const retryCheck = useCallback(
        (isInstant = false) => {
            setIsLoading(true);
            if (!isInstant) {
                attemptsRef.current += 1;
            }

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Exponential backoff: 2s → 4s → 8s → 10s → 10s (capped at 10s)
            const delay = Math.min(
                retryDelayMs * Math.pow(2, attemptsRef.current - 1),
                10_000,
            );
            timeoutRef.current = setTimeout(() => {
                setCurrentSrc(`${src}?retry=${new Date().getTime()}`);
                timeoutRef.current = null;
            }, delay);
        },
        [src, retryDelayMs],
    );

    const handleError = useCallback(() => {
        if (attemptsRef.current < retryCount) {
            retryCheck();
            onDownloadButton?.(false);
        } else {
            setCurrentSrc(fallbackSrc || '');
            setIsLoading(false);
            setError(true);
            onDownloadButton?.(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [retryCheck, retryCount, fallbackSrc]);

    const loadingImage = (
        <div className="border-gray-30 flex h-10 w-auto items-center gap-2 rounded-lg border bg-white p-2 text-xs text-gray-600">
            <Loading
                size={20}
                overrideColor="text-gray-600"
                disableTimeout={true}
            />
            <span className="truncate">
                {attachment?.get('fileName').toLowerCase()}
            </span>
        </div>
    );

    const errorImage = error ? (
        <div className="flex h-auto w-full items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white p-2 text-xs text-gray-600">
            <div className="flex min-w-0 flex-1 gap-2">
                <div className="flex h-4 w-4 items-center justify-center rounded-full p-1">
                    <Icon iconKey="imageView" />
                </div>
                <span className="truncate">
                    {attachment?.get('fileName').toLowerCase()}
                </span>
            </div>
            <button
                aria-label={i18next.t('chat.try_again')}
                className="rounded bg-white text-gray-600 transition hover:text-gray-500"
                onClick={() => retryCheck(true)}
            >
                <Icon iconKey="reload" />
            </button>
        </div>
    ) : null;

    return (
        <div className="flex-start relative z-0 flex min-h-10 min-w-47">
            {isLoading || error ? (
                <div className="absolute inset-0 flex items-center">
                    {isLoading ? loadingImage : errorImage}
                </div>
            ) : null}
            <button
                onClick={onButtonClick}
                onKeyDown={onButtonKeyDown}
                className={`${isLoading || error ? 'invisible' : ''}`}
            >
                <img
                    height={height}
                    width={width}
                    ref={ref}
                    className={className}
                    src={currentSrc}
                    alt={alt}
                    onError={handleError}
                    onLoad={(event) => {
                        setIsLoading(false);
                        setError(false);
                        onLoad?.(event);
                    }}
                />
            </button>
        </div>
    );
};

export default PreviewImageWithRetry;
